import { MapPin, Star, Phone, Globe, TrendingUp, ExternalLink, Building2 } from 'lucide-react'

export default function GoogleMapsPanel({ googleMapsData }) {
  if (!googleMapsData) return null

  const { businesses = [], summary = {}, service, country } = googleMapsData

  if (businesses.length === 0) return null

  const renderStars = (rating) => {
    if (!rating) return <span className="text-xs text-slate-light">Sin rating</span>
    return (
      <span className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-3 h-3 ${i < Math.floor(rating) ? 'text-slate-lighter fill-slate-lighter' : 'text-slate-medium'}`}
          />
        ))}
        <span className="text-xs text-slate-lighter ml-1">{rating}</span>
      </span>
    )
  }

  const opportunityColor = (level) => {
    switch (level) {
      case 'Excelente': return 'text-slate-lighter'
      case 'Buena':     return 'text-slate-light'
      default:          return 'text-slate-medium'
    }
  }

  const competitionColor = (level) => {
    switch (level) {
      case 'Muy Alta': return 'text-red-400'
      case 'Alta':     return 'text-orange-400'
      case 'Media':    return 'text-yellow-400'
      default:         return 'text-slate-lighter'
    }
  }

  return (
    <div className="gradient-glow p-6 rounded-xl card-hover">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <MapPin className="w-5 h-5 text-slate-lighter" />
        <h3 className="text-lg font-bold text-white">Google Maps — Competencia Local</h3>
        {country && (
          <span className="ml-auto text-xs px-2 py-0.5 bg-slate-darker border border-slate-dark/40 text-slate-lighter rounded">
            {country}
          </span>
        )}
      </div>

      {/* Summary stats */}
      {summary && Object.keys(summary).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
            <p className="text-xl font-bold text-slate-lighter">{summary.total_found || 0}</p>
            <p className="text-xs text-slate-light">Negocios</p>
          </div>
          <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
            <p className="text-xl font-bold text-slate-lighter">{summary.avg_rating || 'N/D'}</p>
            <p className="text-xs text-slate-light">Rating promedio</p>
          </div>
          <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
            <p className={`text-base font-bold ${competitionColor(summary.competition_level)}`}>
              {summary.competition_level || 'N/D'}
            </p>
            <p className="text-xs text-slate-light">Competencia</p>
          </div>
          <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
            <p className={`text-base font-bold ${opportunityColor(summary.market_opportunity)}`}>
              {summary.market_opportunity || 'N/D'}
            </p>
            <p className="text-xs text-slate-light">Oportunidad</p>
          </div>
        </div>
      )}

      {/* Business listings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {businesses.slice(0, 8).map((biz, idx) => (
          <div
            key={idx}
            className="p-3 bg-slate-darker border border-slate-dark/20 rounded-lg hover:border-slate-dark/50 transition-all"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-light shrink-0" />
                <p className="text-sm font-semibold text-slate-lightest line-clamp-1">
                  {biz.name}
                </p>
              </div>
              {biz.google_maps_url && biz.google_maps_url !== '#' && (
                <a
                  href={biz.google_maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-light hover:text-slate-lighter transition-colors shrink-0"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>

            <div className="space-y-1.5">
              {/* Rating */}
              <div className="flex items-center justify-between">
                {renderStars(biz.rating)}
                {biz.review_count && (
                  <span className="text-xs text-slate-light">
                    {biz.review_count.toLocaleString()} reseñas
                  </span>
                )}
              </div>

              {/* Price & categories */}
              <div className="flex items-center gap-2">
                {biz.price_level && biz.price_level !== 'N/A' && (
                  <span className="text-xs px-1.5 py-0.5 bg-slate-dark text-slate-lighter rounded">
                    {biz.price_level}
                  </span>
                )}
                {biz.is_open !== undefined && (
                  <span className={`text-xs ${biz.is_open ? 'text-slate-lighter' : 'text-slate-light'}`}>
                    {biz.is_open ? '● Abierto' : '○ Cerrado'}
                  </span>
                )}
                {biz.is_mock && (
                  <span className="text-xs text-slate-medium italic">demo</span>
                )}
              </div>

              {/* Address */}
              {biz.address && (
                <div className="flex items-start gap-1.5">
                  <MapPin className="w-3 h-3 text-slate-medium mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-light line-clamp-1">{biz.address}</p>
                </div>
              )}

              {/* Phone */}
              {biz.phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="w-3 h-3 text-slate-medium shrink-0" />
                  <p className="text-xs text-slate-light">{biz.phone}</p>
                </div>
              )}

              {/* Website */}
              {biz.website && (
                <div className="flex items-center gap-1.5">
                  <Globe className="w-3 h-3 text-slate-medium shrink-0" />
                  <a
                    href={biz.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-slate-lighter hover:underline truncate max-w-[180px]"
                  >
                    {biz.website.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer insight */}
      <div className="mt-4 p-3 bg-slate-darker rounded-lg border border-slate-dark/20">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-slate-light shrink-0" />
          <p className="text-xs text-slate-300">
            <span className="font-semibold text-slate-lighter">Google Maps:</span>{' '}
            {summary.avg_review_count
              ? `Promedio de ${summary.avg_review_count} reseñas por negocio. `
              : ''}
            {summary.market_opportunity === 'Excelente'
              ? 'Mercado poco saturado con ratings bajos — oportunidad de diferenciación clara.'
              : summary.market_opportunity === 'Buena'
              ? 'Competencia presente pero con espacio para un servicio de calidad superior.'
              : 'Mercado competitivo. Enfócate en una especialización de nicho para diferenciarte.'}
          </p>
        </div>
      </div>
    </div>
  )
}
